// modals/salesReturns/ViewSaleReturn.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {     
    Printer, 
    Download, 
    Clock,   
    CheckCircle,
    XCircle,
    AlertTriangle,
    RefreshCw,
    CreditCard,
    X
} from 'react-feather';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleReturnActions } from '../../action/saleReturnActions';

const ViewSaleReturn = ({ returnData, onClose }) => {
    const [returnDetails, setReturnDetails] = useState(returnData);
    const [items, setItems] = useState([]);
    const [creditInfo, setCreditInfo] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (returnData && !returnData.items) {
            loadReturnDetails();
        } else {
            setItems(returnData.items || []);
            setReturnDetails(returnData);
        }
    }, [returnData]);

    const loadReturnDetails = async () => {
        try {
            setLoading(true);
            const result = await saleReturnActions.getReturn(returnData.id);
            
            if (result.success) {
                const returnData = result.data.return || result.data;
                const itemsData = result.data.items || [];
                
                // Ensure each item has proper tax calculation
                const processedItems = itemsData.map(item => {
                    // If total_cost is zero but we have tax_amount, calculate it
                    if (item.total_cost === 0 && item.tax_amount > 0) {
                        item.total_cost = (item.unit_price * item.quantity) + item.tax_amount;
                    }
                    return item;
                });
                
                setReturnDetails(returnData);
                setItems(processedItems);
                setCreditInfo(result.data.credit || null);
            }
        } catch (error) {
            console.error('Error loading return details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    const formatDateTime = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY hh:mm A') : 'N/A';
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': { class: 'bg-warning', icon: <Clock size={14} /> },
            'approved': { class: 'bg-info', icon: <CheckCircle size={14} /> },
            'received': { class: 'bg-primary', icon: <RefreshCw size={14} /> },
            'completed': { class: 'bg-success', icon: <CheckCircle size={14} /> },
            'rejected': { class: 'bg-danger', icon: <XCircle size={14} /> },
            'cancelled': { class: 'bg-secondary', icon: <XCircle size={14} /> }
        };
        const badge = badges[status?.toLowerCase()] || { class: 'bg-secondary', icon: <AlertTriangle size={14} /> };
        return (
            <span className={`badge ${badge.class}`}>
                {badge.icon} {status?.toUpperCase()}
            </span>
        );
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            'unpaid': { class: 'bg-danger', icon: <XCircle size={14} /> },
            'partial': { class: 'bg-warning', icon: <AlertTriangle size={14} /> },
            'paid': { class: 'bg-success', icon: <CheckCircle size={14} /> }
        };
        const badge = badges[status?.toLowerCase()] || { class: 'bg-secondary', icon: null };
        return (
            <span className={`badge ${badge.class}`}>
                {badge.icon} {status?.toUpperCase()}
            </span>
        );
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownload = async () => {
        try {
            Swal.fire('Info', 'Download feature coming soon', 'info');
        } catch (error) {
            console.error('Error downloading:', error);
        }
    };

    if (!returnDetails) return null;

    // Calculate totals from items
    const calculatedSubtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const calculatedTax = items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
    const calculatedGrandTotal = items.reduce((sum, item) => sum + (item.total_cost || 0), 0);

    // Use provided values or calculated ones
    const displaySubtotal = returnDetails.subtotal || calculatedSubtotal;
    const displayTax = returnDetails.tax || calculatedTax;
    const displayGrandTotal = returnDetails.grand_total || calculatedGrandTotal;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Sale Return Details - {returnDetails.reference_no}
                        </h5>
                        <div className="d-flex gap-2">
                            <button 
                                type="button" 
                                className="btn btn-outline-primary btn-sm"
                                onClick={handlePrint}
                                title="Print"
                            >
                                <Printer size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline-success btn-sm"
                                onClick={handleDownload}
                                title="Download"
                            >
                                <Download size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>
                    
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading details...</p>
                            </div>
                        ) : (
                            <>
                                {/* Header Info */}
                                <div className="row mb-4">
                                    <div className="col-md-8">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Return Reference</p>
                                                        <h6 className="fw-bold">{returnDetails.reference_no}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Original Invoice</p>
                                                        <h6>{returnDetails.original_invoice_no || 'N/A'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Return Date</p>
                                                        <h6>{formatDate(returnDetails.return_date)}</h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created By</p>
                                                        <h6>{returnDetails.created_by || 'Admin'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created At</p>
                                                        <h6>{formatDateTime(returnDetails.created_at)}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Status / Refund</p>
                                                        <div className="d-flex gap-2">
                                                            {getStatusBadge(returnDetails.status)}
                                                            {getPaymentStatusBadge(returnDetails.payment_status)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card bg-primary text-white">
                                            <div className="card-body">
                                                <h6 className="text-white mb-2">Return Summary</h6>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span>Subtotal:</span>
                                                    <span className="fw-bold">{formatCurrency(displaySubtotal)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-1">
                                                    <span>Tax:</span>
                                                    <span className="fw-bold">{formatCurrency(displayTax)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between pt-2 border-top border-white border-opacity-25">
                                                    <span className="fw-bold">Grand Total:</span>
                                                    <span className="fw-bold fs-5">{formatCurrency(displayGrandTotal)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Info */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Customer Information</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Name</p>
                                                <p className="fw-bold">{returnDetails.customer_name || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Phone</p>
                                                <p>{returnDetails.customer_phone || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Email</p>
                                                <p>{returnDetails.customer_email || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Address</p>
                                                <p>{returnDetails.customer_address || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Items Table */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Return Items</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="table-responsive">
                                            <table className="table table-bordered mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Product Code</th>
                                                        <th>Product Name</th>
                                                        <th className="text-end">Qty</th>
                                                        <th className="text-end">Rate</th>
                                                        <th className="text-end">Tax %</th>
                                                        <th className="text-end">Tax Amount</th>
                                                        <th className="text-end">Total</th>
                                                        <th>Reason</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="9" className="text-center py-4">
                                                                No items found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        items.map((item, index) => {
                                                            // Calculate per-item values
                                                            const itemSubtotal = item.unit_price * item.quantity;
                                                            const itemTax = item.tax_amount || 0;
                                                            const itemTotal = item.total_cost || (itemSubtotal + itemTax);
                                                            
                                                            return (
                                                                <tr key={item.id || index}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{item.product_code || item.sku || '-'}</td>
                                                                    <td>
                                                                        <strong>{item.product_name}</strong>
                                                                    </td>
                                                                    <td className="text-end">{item.quantity}</td>
                                                                    <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                                    <td className="text-end">{item.tax_percent || 0}%</td>
                                                                    <td className="text-end">{formatCurrency(itemTax)}</td>
                                                                    <td className="text-end fw-bold">{formatCurrency(itemTotal)}</td>
                                                                    <td>{item.reason || '-'}</td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Subtotal:</th>
                                                        <th className="text-end">{formatCurrency(displaySubtotal)}</th>
                                                        <th></th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end">Tax Total:</th>
                                                        <th className="text-end">{formatCurrency(displayTax)}</th>
                                                        <th></th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="7" className="text-end fw-bold">Grand Total:</th>
                                                        <th className="text-end fw-bold text-primary">
                                                            {formatCurrency(displayGrandTotal)}
                                                        </th>
                                                        <th></th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* Refund Summary */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="card">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">Refund Summary</h6>
                                            </div>
                                            <div className="card-body">
                                                <table className="table table-sm">
                                                    <tbody>
                                                        <tr>
                                                            <td>Return Total:</td>
                                                            <td className="text-end fw-bold">{formatCurrency(displayGrandTotal)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Refund Method:</td>
                                                            <td className="text-end">
                                                                <span className="badge bg-info text-white">
                                                                    {returnDetails.refund_method || 'N/A'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>Refunded Amount:</td>
                                                            <td className="text-end text-success fw-bold">
                                                                {formatCurrency(returnDetails.paid_amount || 0)}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>Pending Refund:</td>
                                                            <td className="text-end text-danger fw-bold">
                                                                {formatCurrency(returnDetails.due_amount || 0)}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td>Refund Status:</td>
                                                            <td className="text-end">
                                                                {getPaymentStatusBadge(returnDetails.payment_status)}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        {returnDetails.reason && (
                                            <div className="card mb-3">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Return Reason</h6>
                                                </div>
                                                <div className="card-body">
                                                    <p className="mb-0">{returnDetails.reason}</p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {returnDetails.notes && (
                                            <div className="card mb-3">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">Additional Notes</h6>
                                                </div>
                                                <div className="card-body">
                                                    <p className="mb-0">{returnDetails.notes}</p>
                                                </div>
                                            </div>
                                        )}

                                        {creditInfo && (
                                            <div className="card border-info">
                                                <div className="card-header bg-info text-white">
                                                    <h6 className="mb-0 text-white">
                                                        <CreditCard size={16} className="me-2" />
                                                        Store Credit
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-bold">Amount:</span>
                                                        <span className="text-success fw-bold fs-5">
                                                            {formatCurrency(creditInfo.amount)}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                                        <span>Status:</span>
                                                        <span className={`badge ${creditInfo.status === 'applied' ? 'bg-success' : 'bg-warning'}`}>
                                                            {creditInfo.status}
                                                        </span>
                                                    </div>
                                                    {creditInfo.applied_at && (
                                                        <small className="text-muted d-block mt-2">
                                                            Applied: {formatDateTime(creditInfo.applied_at)}
                                                        </small>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <X size={14} className="me-2" />
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewSaleReturn.propTypes = {
    returnData: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewSaleReturn;